import globalCourseStore from '../../../../../../../../stores/global-course-store';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useEffect, useRef, useState } from 'react';
import { UserSummaryType } from '../../../../../../../../api/types/user/user-summary.type';
import {
  ClassSchedule,
  ExamMetadata,
  ExamSessionDetails,
} from '../../../../../../../../api/types/programs/course/exams/response/exam-metadata';
import { Checkbox } from 'primereact/checkbox';
import { upsertStudentsEnrollmentOperations } from './operations/upsert-students-enrollment-operations';
import { Button } from 'primereact/button';
import globalProgramStore from '../../../../../../../../stores/program-store';
import { EnrollmentWithUserDb } from '../../../../../../../../api/types/programs/course/exams/exam-sessions-enrollment-entity';
import { RequestEnrollment } from '../../../../../../../../api/types/programs/course/exams/requests/request-exam-enrollment-update';
import { EditNumber } from '../../../../../../../../component/ui/edit-text/edit-number';
import { Calendar } from 'primereact/calendar';
import { AttendanceType } from '../../../../../../../../api/types/lecturers/attendance/attendance-type';

interface StudentsEnrollmentDatamodel {
  examDetails: ExamMetadata;
  examSession: ExamSessionDetails | null;
  setExamDetails: (sessions: ExamSessionDetails[]) => any;
}

export const StudentsEnrollment: React.FC<StudentsEnrollmentDatamodel> = ({
  examDetails,
  examSession,
  setExamDetails,
}) => {
  // This will hold the mapping of studentId to classId
  const [studentClassRegistrations, setStudentClassRegistrations] = useState<
    Record<number, RequestEnrollment>
  >({});

  const initialEnrollmentState = useRef<Record<number, RequestEnrollment>>({});

  const initializeStudentRegistrations = (
    examSession: ExamSessionDetails,
  ): Record<number, RequestEnrollment> => {
    const initialRegistrations: Record<number, RequestEnrollment> = {};

    examSession.classesSchedule.forEach((classSchedule: ClassSchedule) => {
      classSchedule.enrollment.forEach((enrollment: EnrollmentWithUserDb) => {
        initialRegistrations[enrollment.studentId] = {
          studentId: enrollment.studentId,
          scheduleId: enrollment.scheduleId,
          grade: enrollment.grade ?? undefined,
          disableSelfEnrollment: enrollment.disableSelfEnrollment ?? undefined,
          attended: enrollment.attended ?? undefined,
          selfEnrollmentDueDate: enrollment.selfEnrollmentDueDate ?? undefined,
        };
      });
    });

    return initialRegistrations;
  };

  useEffect(() => {
    if (examSession) {
      const initialState = initializeStudentRegistrations(examSession);
      initialEnrollmentState.current = initialState; // Store for comparison
      setStudentClassRegistrations(initialState); // Update current state
    }
  }, [examSession]);

  const updateStudentRegistration = (
    studentId: number,
    classId: number | null,
  ) => {
    setStudentClassRegistrations((prev) => {
      const newRegistrations = { ...prev };

      if (classId === null) {
        // If classId is null, remove the registration
        delete newRegistrations[studentId];
      } else {
        // Update or add the registration
        newRegistrations[studentId] = {
          ...prev[studentId], // Preserve existing fields
          studentId,
          scheduleId: classId,
        };
      }

      return newRegistrations;
    });
  };

  const updateStudentGrade = (studentId: number, grade: number | null) => {
    setStudentClassRegistrations((prev) => {
      const newRegistrations = { ...prev };

      // If there's no existing registration for this student, create one
      if (!newRegistrations[studentId]) {
        newRegistrations[studentId] = {
          studentId,
          grade,
        };
      } else {
        // Update existing registration
        newRegistrations[studentId] = {
          ...newRegistrations[studentId],
          grade,
        };
      }

      return newRegistrations;
    });
  };

  const updateStudentDisableSelfRegistration = (
    studentId: number,
    disableSelfEnrollment: boolean | undefined,
  ) => {
    setStudentClassRegistrations((prev) => {
      const newRegistrations = { ...prev };

      // If there's no existing registration for this student, create one
      if (!newRegistrations[studentId]) {
        newRegistrations[studentId] = {
          studentId,
          disableSelfEnrollment,
        };
      } else {
        // Update existing registration
        newRegistrations[studentId] = {
          ...newRegistrations[studentId],
          disableSelfEnrollment,
        };
      }

      return newRegistrations;
    });
  };

  const updateStudentSelfRegistrationDueDate = (
    studentId: number,
    dueDate: Date | undefined,
  ) => {
    setStudentClassRegistrations((prev) => {
      const newRegistrations = { ...prev };

      // If there's no existing registration for this student, create one
      if (!newRegistrations[studentId]) {
        newRegistrations[studentId] = {
          studentId,
          selfEnrollmentDueDate: dueDate,
        };
      } else {
        // Update existing registration
        newRegistrations[studentId] = {
          ...newRegistrations[studentId],
          selfEnrollmentDueDate: dueDate,
        };
      }

      return newRegistrations;
    });
  };

  const updateStudentAttendance = (
    studentId: number,
    attendance: AttendanceType,
  ) => {
    setStudentClassRegistrations((prev) => {
      const newRegistrations = { ...prev };

      // If there's no existing registration for this student, create one
      if (!newRegistrations[studentId]) {
        newRegistrations[studentId] = {
          studentId,
          attended: attendance,
        };
      } else {
        // Update existing registration
        newRegistrations[studentId] = {
          ...newRegistrations[studentId],
          attended: attendance,
        };
      }

      return newRegistrations;
    });
  };

  const currentCourse = globalCourseStore.getCurrentCourse();
  const [loading, setLoading] = useState(false);
  const handleOnApprove = async () => {
    const currentProgramId = globalProgramStore.getCurrentId();

    await upsertStudentsEnrollmentOperations(
      currentProgramId,
      currentCourse?.metadata.id,
      examDetails.id,
      examSession?.id,
      initialEnrollmentState.current,
      studentClassRegistrations,
      setExamDetails,
      setLoading,
    );
  };

  return (
    <div>
      <DataTable
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
        selectionMode={'multiple'}
        loading={examSession === null}
        multiple={true}
        groupRowsBy="dsf"
        value={currentCourse?.details?.students}
        scrollable
        onSelectionChange={(e) => {
          //setSelectedUsers(e.value as UserSummaryType[]);
        }}
        dataKey="id"
        tableStyle={{
          direction: 'rtl',
          marginTop: '16px',
        }}
      >
        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="firstName"
          header="שם"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          body={(data: UserSummaryType) => (
            <div>
              {data.firstName} {data.lastName}
            </div>
          )}
        />

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="firstName"
          header="ת.ז"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          body={(data: UserSummaryType) => <div>{data.nationalId}</div>}
        />

        {examSession &&
          examSession.classesSchedule.map((classItem: ClassSchedule) => (
            <Column
              key={classItem.id}
              style={{ textAlign: 'right', height: '40px' }}
              header={classItem.className}
              headerStyle={{
                height: '45px',
                maxHeight: '45px',
                padding: '0',
                paddingRight: '16.5px',
              }}
              body={(data: UserSummaryType) => (
                <Checkbox
                  checked={
                    studentClassRegistrations[data.id]?.scheduleId ===
                    classItem.scheduleId
                  }
                  onChange={(e) => {
                    updateStudentRegistration(
                      data.id,
                      e.checked ? classItem.scheduleId : null,
                    );
                  }}
                />
              )}
            />
          ))}

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          header={'חסימת הרשמה עצמית'}
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          body={(data: UserSummaryType) => (
            <Checkbox
              checked={
                studentClassRegistrations[data.id]?.disableSelfEnrollment ??
                false
              }
              onChange={(e) =>
                updateStudentDisableSelfRegistration(data.id, e.checked)
              }
            />
          )}
        />

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          header={'תאריך אחרון להרשמה'}
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          body={(data: UserSummaryType) => (
            <Calendar
              style={{
                direction: 'ltr',
                height: '36px',
              }}
              className="calendar-button-small"
              inputStyle={{ direction: 'rtl' }}
              value={studentClassRegistrations[data.id]?.selfEnrollmentDueDate}
              onChange={(e) =>
                updateStudentSelfRegistrationDueDate(
                  data.id,
                  e?.target?.value ?? undefined,
                )
              }
              showIcon={true}
              iconPos={'right'}
              yearNavigator={true}
              yearRange={'2023:2030'}
            />
          )}
        />

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          header={'נוכחות'}
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          body={(data: UserSummaryType) => (
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div>
                <Checkbox
                  checked={
                    studentClassRegistrations[data.id]?.attended ===
                    AttendanceType.Attended
                  }
                  onChange={(e) =>
                    updateStudentAttendance(data.id, AttendanceType.Attended)
                  }
                />
                <label>נוכח</label>
              </div>

              <div>
                <Checkbox
                  checked={
                    studentClassRegistrations[data.id]?.attended ===
                    AttendanceType.Late
                  }
                  onChange={(e) =>
                    updateStudentAttendance(data.id, AttendanceType.Late)
                  }
                />
                <label>איחור</label>
              </div>

              <div>
                <Checkbox
                  checked={
                    studentClassRegistrations[data.id]?.attended ===
                    AttendanceType.Missed
                  }
                  onChange={(e) =>
                    updateStudentAttendance(data.id, AttendanceType.Missed)
                  }
                />
                <label>חיסור</label>
              </div>
            </div>
          )}
        />

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          header={'ציון'}
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            paddingRight: '16.5px',
          }}
          body={(data: UserSummaryType) => (
            <EditNumber
              value={studentClassRegistrations[data.id]?.grade ?? undefined}
              onChange={(value: number) => updateStudentGrade(data.id, value)}
            />
          )}
        />
      </DataTable>

      <Button
        label={'אישור2'}
        style={{ height: '30px', marginTop: '8px' }}
        loading={loading}
        onClick={handleOnApprove}
      />
    </div>
  );
};
